@import "tailwindcss";

/*
 * Veritact design tokens.
 *
 * Brand identity: home-services consumer, "we got your install ready."
 * Visual posture: soft minimalism on warm cream, with a deep confident
 * blue carrying the brand through CTAs and signal moments alike. The
 * blue lives several stops down the ramp from the bright accent so it
 * reads as sophisticated home-services, not big-box-retailer saturated
 * fill. Deep navy ink anchors the typography.
 *
 * Palette drawn from the Veritact Brand Kit:
 *   #1E3F95 — primary deep blue, used for CTAs, focus, brand mark, icons
 *   #2A52D6 — brighter "signal" blue, used for highlights / accent tints
 *   #0B1F3A — deep navy ink, headlines + body text
 *   #F4F2EC — warm cream background
 *   #7A8595 / #C9CED6 — cool grey secondary text + borders
 *
 * All semantic shadcn tokens (--background, --foreground, --primary, etc.)
 * are wired here so the dropped-in shadcn components inherit the brand
 * automatically. The demo is locked to light mode — pitch surfaces must
 * look the same on every device, regardless of OS theme. The browser
 * `color-scheme: light` declaration + `data-theme="light"` on <html>
 * neutralize the system dark preference.
 *
 * Typography: Inter (already preloaded via index.html). One stack, one
 * weight scale (400 → 500 → 600 → 700).
 */

@theme {
  /* Veritact accent — confident blue (~#2A52D6) in oklch. */
  --color-brand-50:  oklch(96% 0.025 265);
  --color-brand-100: oklch(92% 0.050 265);
  --color-brand-200: oklch(85% 0.090 265);
  --color-brand-300: oklch(75% 0.135 265);
  --color-brand-400: oklch(64% 0.170 265);
  --color-brand-500: oklch(55% 0.190 265);
  --color-brand-600: oklch(48% 0.195 265);
  --color-brand-700: oklch(40% 0.170 265);
  --color-brand-800: oklch(30% 0.130 265);
  --color-brand-900: oklch(22% 0.080 265);

  /* Cool neutral ramp — anchored to the brand navy (~#0B1F3A → #7A8595 → #C9CED6). */
  --color-ink-50:  oklch(98% 0.005 250);
  --color-ink-100: oklch(95% 0.010 250);
  --color-ink-200: oklch(90% 0.012 250);
  --color-ink-300: oklch(83% 0.014 250);
  --color-ink-400: oklch(70% 0.020 252);
  --color-ink-500: oklch(57% 0.023 254);
  --color-ink-700: oklch(35% 0.035 256);
  --color-ink-900: oklch(22% 0.050 258);

  /* Warm cream surfaces (~#F4F2EC). */
  --color-cream-50:  oklch(98% 0.004 86);
  --color-cream-100: oklch(96% 0.006 86);

  /* Status palette. */
  --color-ok-500:   oklch(62% 0.130 155);
  --color-warn-500: oklch(75% 0.130 75);
  --color-err-500:  oklch(60% 0.190 25);

  --font-sans: "Inter", system-ui, sans-serif;

  --radius-xl2: 1.25rem;
  --radius-phone: 2.5rem;
}

/*
 * shadcn semantic tokens (light mode default).
 * Names match the shadcn registry so installed components pick them up.
 */
:root {
  color-scheme: light;

  /* Off-white with the faintest cream warmth — reads as neutral, not eggshell. */
  --background: oklch(98.5% 0.003 86);
  --foreground: oklch(22% 0.050 258);

  --card: oklch(100% 0 0);
  --card-foreground: oklch(22% 0.050 258);

  --popover: oklch(100% 0 0);
  --popover-foreground: oklch(22% 0.050 258);

  /* Primary = deep Veritact blue (~#1E3F95). Several stops below the bright
     signal blue so CTAs read as sophisticated, not retail saturated. */
  --primary: oklch(38% 0.155 266);
  --primary-foreground: oklch(99% 0.003 86);

  --secondary: oklch(93% 0.010 250);
  --secondary-foreground: oklch(35% 0.035 256);

  --muted: oklch(93% 0.010 250);
  --muted-foreground: oklch(57% 0.023 254);

  /* Accent = soft blue tint with the deep primary blue as the foreground —
     icons, badges, ScanResult/AddOn circles all match the button identity. */
  --accent: oklch(93% 0.030 266);
  --accent-foreground: oklch(38% 0.155 266);

  --destructive: oklch(60% 0.190 25);
  --destructive-foreground: oklch(99% 0.003 86);

  --border: oklch(88% 0.014 250);
  --input: oklch(88% 0.014 250);
  /* Focus ring uses the brighter signal blue for visibility against the
     deeper primary fill. */
  --ring: oklch(48% 0.195 265);

  --radius: 0.75rem;
}

/*
 * Tell Tailwind v4 about the semantic tokens so utilities like `bg-background`,
 * `text-foreground`, `border-border`, `ring-ring` map to the CSS vars above.
 */
@theme inline {
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-popover: var(--popover);
  --color-popover-foreground: var(--popover-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-destructive: var(--destructive);
  --color-destructive-foreground: var(--destructive-foreground);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-ring: var(--ring);
}

@layer base {
  html, body, #root { height: 100%; }
  body {
    @apply bg-background text-foreground antialiased;
    font-family: 'Inter', system-ui, sans-serif;
  }
  /* Lock the page scroll on desktop — the phone-frame inner viewport has
     its own overflow-y-auto, and the marketing column should never push
     the window beyond 100vh. Mobile keeps its natural scroll behavior. */
  @media (min-width: 768px) {
    html, body { overflow: hidden; }
  }
  h1 { @apply text-3xl font-semibold leading-tight; }
  h2 { @apply text-2xl font-semibold; }
  button:focus-visible { @apply outline-2 outline-ring outline-offset-2; }

  /* Animations capped at 200ms per Veritact principles. */
  * {
    transition-duration: 200ms;
  }
}

/*
 * Desktop phone-frame typography scale.
 *
 * The phone preview on desktop is narrower than a real iPhone (393 CSS px)
 * whenever the viewport is shorter than ~920px — the frame is capped at
 * 92vh and the 393/852 aspect ratio drives the width down with it. The
 * Tailwind text scale (rem-based, rooted at <html>) doesn't shrink with
 * the frame, so a 30px headline that reads fine on a real device gets
 * jammed against the bezel in the preview.
 *
 * Override the Tailwind text utilities and base headings inside the phone
 * frame only, at ~80% of their normal size. This is desktop-preview only:
 * real-phone users get the original scale.
 */
.desktop-phone-typography {
  --text-xs: 0.5625rem;       /* 9px */
  --text-xs--line-height: 0.8125rem;
  --text-sm: 0.6875rem;       /* 11px */
  --text-sm--line-height: 0.9375rem;
  --text-base: 0.75rem;       /* 12px */
  --text-base--line-height: 1.0625rem;
  --text-lg: 0.8125rem;       /* 13px */
  --text-lg--line-height: 1.125rem;
  --text-xl: 0.875rem;        /* 14px */
  --text-xl--line-height: 1.1875rem;
  --text-2xl: 1rem;           /* 16px */
  --text-2xl--line-height: 1.3125rem;
  --text-3xl: 1.1875rem;      /* 19px */
  --text-3xl--line-height: 1.5rem;
  --text-4xl: 1.375rem;       /* 22px */
  --text-4xl--line-height: 1.75rem;
}

.desktop-phone-typography h1 {
  font-size: var(--text-3xl);
  line-height: var(--text-3xl--line-height);
}
.desktop-phone-typography h2 {
  font-size: var(--text-2xl);
  line-height: var(--text-2xl--line-height);
}

/*
 * Desktop phone-frame: clear the dynamic-island stand-in.
 *
 * The island is positioned at top: 0.5rem with height 1.5rem — its lower
 * edge sits 2rem from the top of the frame. On real iOS the safe-area
 * inset pushes the header below the cutout; in the desktop preview the
 * env var resolves to 0, so the header text collides with the bezel.
 *
 * Use margin (not padding) so the header keeps its fixed h-14 height —
 * padding inside h-14 would crush the title's line-box and let the text
 * bleed past the border-bottom. This class only renders inside the
 * desktop preview, so real iOS never sees it.
 */
.desktop-phone-typography header {
  margin-top: 2.25rem;
}

